import { useEffect, useRef, useState } from "react";
import { searchCodes, type CodeKind, type CodeResult } from "./search-codes";

// Care Finder's 300ms / three-character search threshold, with abortable
// REST requests instead of its feature-specific Masonry reload action.
export const useCodeSearch = (
  kind: CodeKind,
  query: string,
  enabled: boolean
) => {
  const [items, setItems] = useState<CodeResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const request = useRef<AbortController>();
  const currentQuery = useRef("");
  const pending = useRef(false);

  const cancel = () => {
    request.current?.abort();
    pending.current = false;
  };

  const load = async () => {
    if (pending.current) return;
    pending.current = true;
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setFailed(false);
    try {
      const result = await searchCodes(
        kind,
        currentQuery.current,
        controller.signal
      );
      if (controller.signal.aborted) return;
      setItems(result);
    } catch {
      if (!controller.signal.aborted) setFailed(true);
    } finally {
      if (!controller.signal.aborted) {
        pending.current = false;
        setLoading(false);
      }
    }
  };
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    cancel();
    currentQuery.current = query.trim();
    setItems([]);
    setFailed(false);
    const eligible = enabled && Array.from(query.trim()).length >= 3;
    setLoading(eligible);
    const timer = eligible
      ? setTimeout(() => {
          loadRef.current();
        }, 300)
      : undefined;
    return () => {
      clearTimeout(timer);
      cancel();
    };
  }, [kind, query, enabled]);

  return {
    items,
    loading,
    failed,
    cancel,
    retry: load,
  };
};
